import React from "react";
import { Loader2, Wallet } from "lucide-react";
import { useScratchCard } from "../hooks/useScratchCard";

// Presentational scratch card. All canvas/scratch/reward logic lives in useScratchCard.
export default function ScratchCard({ token, onDone, onReveal }) {
  const { canvasRef, status, errMsg, amount, ticket, sparks,
          won, isTicket, positive, down, move, up, finish } = useScratchCard(token, onReveal);

  return (
    <div className="w-full">
      <p className="mb-3 text-center font-mono text-xs uppercase tracking-widest text-slate-500">
        {status === "revealed" ? "Your reward" : "Scratch to reveal"}
      </p>

      <div className="relative mx-auto h-44 w-full max-w-xs overflow-hidden rounded-2xl border border-slate-700">
        {/* reward underneath */}
        <div className={`absolute inset-0 flex flex-col items-center justify-center text-center px-4 ${positive ? "bg-gradient-to-br from-emerald-500/25 to-slate-900" : "bg-slate-900"}`}>
          {status === "loading" && <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />}
          {status === "error" && <p className="px-6 text-sm text-rose-300">{errMsg}</p>}
          {(status === "ready" || status === "revealed") && (
            won ? (
              <>
                <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400/80">You won</span>
                <span className="font-heading text-5xl font-extrabold text-emerald-400" style={{ transform: status === "revealed" ? "scale(1)" : "scale(.95)", transition: "transform .3s" }}>₹{(amount / 100).toLocaleString("en-IN")}</span>
                <span className="mt-1 text-xs text-slate-400">Instant cashback</span>
              </>
            ) : isTicket ? (
              <>
                <span className="text-3xl">🎉</span>
                <span className="mt-1 font-heading text-lg font-bold text-emerald-300">Congratulations!</span>
                <span className="text-xs text-slate-300">You received a Lucky Draw Ticket</span>
                <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono text-sm font-bold tracking-wider text-emerald-300" data-testid="scratch-ticket-code">🎟️ {ticket?.ticketCode || "LD-TICKET"}</span>
              </>
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
            )
          )}
        </div>
        {/* scratch coating */}
        {status === "ready" && (
          <>
            <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
              className="absolute inset-0 h-full w-full cursor-grab touch-none" style={{ touchAction: "none" }} data-testid="scratch-canvas" />
            {/* premium shine sweep across the coating */}
            <div className="scratch-shine pointer-events-none absolute inset-0" />
            {/* golden glitter particles */}
            {sparks.map((p) => (
              <span key={p.id} className="scratch-spark pointer-events-none absolute" style={{ left: p.x, top: p.y, "--dx": `${p.dx}px`, "--dy": `${p.dy}px` }} />
            ))}
          </>
        )}
      </div>

      {status === "ready" && (
        <button data-testid="reveal-fallback" onClick={finish} className="mx-auto mt-3 block text-xs text-slate-500 underline hover:text-slate-300">
          Having trouble scratching? Reveal card
        </button>
      )}
      {status === "revealed" && (
        <div className="mt-4 text-center">
          <p className="text-sm text-emerald-300">{won ? "🎉 Cashback added to your wallet" : isTicket ? "🎟️ Saved to My Lucky Tickets" : "Token checked successfully"}</p>
          <button data-testid="view-wallet-after" onClick={() => onDone(true)} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400">
            <Wallet className="h-4 w-4" /> {isTicket ? "View my tickets" : "View my wallet"}
          </button>
        </div>
      )}
      {status === "error" && (
        <button data-testid="scratch-error-close" onClick={() => onDone(false)} className="mx-auto mt-4 block rounded-full border border-slate-700 px-5 py-2 text-sm text-slate-300">Close</button>
      )}
    </div>
  );
}
